import { useCallback, useEffect, useRef } from 'react';
import { useSelector, type DefaultRootState } from 'react-redux';
import { appSlice, selectAppState } from '@bbkAdminRedux/app/app.slice';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import { useSignInRedirect } from '@bbkAdminRedux/app/useSignInRedirect';
import { resolveActiveContext } from '@bbkAdminRedux/app/utils';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { appPersistSlice } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectAuth } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { clearSessionMetadata } from '@bbkAdminRedux/rtkq/clear-session-metadata';
import { selectRememberedActiveContext } from '@bbkAdminRedux/rtkq/tab-persist/tab-persist.slice';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { hardRedirect } from '@bbkAdminUtils/browser-navigation';
import { type Maybe } from '@bbkAdminUtils/utility-types';
import { useLocation } from '@bbkShared/react-router';

/**
 * The context to apply, already resolved from the location by
 * `useSyncActiveContext`. `appInit` only applies it, so the value that was
 * gated on is exactly the value that gets stored.
 */
type ActiveContextTarget = {
  companyKey?: string;
  appKey?: string;
  redirect?: string;
};

export const useAppInit = () => {
  const dispatch = useAppDispatch();
  const { url: signInRedirectUri } = useSignInRedirect();
  // Local non-secret session metadata. Its presence lets us detect a stale
  // session when the `/api/current_user` probe returns no user (the credential
  // itself is an unreadable HttpOnly cookie).
  //
  // Read through a ref so the returned callback keeps a stable identity. Sign-in
  // writes this metadata, and AuthRoot's boot probe effect depends on the
  // callback: a new identity re-runs that probe mid-sign-in, and the resulting
  // `appInit(user)` can hard-navigate away while the company-creation POSTs are
  // still in flight.
  const staleSession = useSelector(selectAuth);
  const staleSessionRef = useRef(staleSession);
  staleSessionRef.current = staleSession;

  // Same reason, plus one of its own: the sign-in URL carries the page the user
  // is on, so it changes on every navigation. Reassign on each render rather
  // than capturing once, or the stale-session bounce would send the user back
  // to whichever page happened to be current when the callback was created.
  const signInRedirectUriRef = useRef(signInRedirectUri);
  signInRedirectUriRef.current = signInRedirectUri;

  return useCallback(
    (user: Maybe<ActiveUser>, target?: ActiveContextTarget) => {
      BrightbackSession.clearLegacyActiveContext();

      if (user) {
        const { companyKey, appKey, redirect } = target ?? {};

        dispatch(
          appSlice.actions.initializeActiveContext({
            activeCompanyKey: companyKey,
            activeAppKey: appKey,
          })
        );
        if (companyKey) {
          dispatch(
            appPersistSlice.actions.saveLastActive({
              email: user.email,
              companyKey,
              appKey,
            })
          );
        }
        if (redirect) hardRedirect(redirect);
      } else if (staleSessionRef.current) {
        // The probe returned no user but stale local session metadata is still
        // present: the server session is gone, so drop local state and bounce
        // to sign-in with a security notice. No CBIDP server logout is issued
        // for an already-ended session.
        dispatch(appSlice.actions.clearActiveContext());
        BrightbackSession.setAuthAlert('Please log in again for security');
        clearSessionMetadata(dispatch);
        hardRedirect(signInRedirectUriRef.current);
      }
    },
    [dispatch]
  );
};

/**
 * Keeps the stored active context in step with the URL, and reports whether it
 * still has to. The returned flag gates rendering, so no page ever renders
 * against a tenant the current location has already moved on from.
 *
 * This is the only place that resolves the context for a signed-in user.
 */
export const useSyncActiveContext = (user: Maybe<ActiveUser>): boolean => {
  const appInit = useAppInit();
  const { pathname } = useLocation();
  const remembered = useSelector((state: DefaultRootState) =>
    selectRememberedActiveContext(state, user?.email)
  );
  const { activeCompanyKey, activeAppKey, loadedNecessaryData } =
    useSelector(selectAppState);

  // `selectUser` is typed non-null but is undefined until the boot probe
  // resolves, and `resolveActiveContext` dereferences `user.companies`.
  const resolved = user
    ? resolveActiveContext(user, pathname, remembered)
    : undefined;
  const companyKey = resolved?.company?.internal_name;
  const appKey = resolved?.app?.encoded_id;
  const redirect = resolved?.redirect;

  // `redirect` is load-bearing on its own: /company/acme/app/missing resolves
  // keys that can already match while still asking to be redirected.
  const needsSync =
    !!resolved &&
    (!loadedNecessaryData ||
      activeCompanyKey !== companyKey ||
      activeAppKey !== appKey ||
      Boolean(redirect));

  // The resolved target is passed in rather than re-derived, so that every
  // input is a dependency. `needsSync` alone would stay `true` across two
  // navigations that both need syncing, and the second one would never run.
  useEffect(() => {
    if (!user || !needsSync) return;
    appInit(user, { companyKey, appKey, redirect });
  }, [appInit, appKey, companyKey, needsSync, redirect, user]);

  return needsSync;
};
